import React, { createContext, useContext, useState, useEffect } from 'react';
import fr from '../locales/fr';
import zh from '../locales/zh';
import { useAuth } from './AuthContext';

const LanguageContext = createContext();

const LOCAL_STORAGE_KEY = 'enovadent_language';

const dictionaries = {
    fr,
    zh
};

export const LanguageProvider = ({ children }) => {
    const { profile, isProducer } = useAuth();

    // Initial state: check localStorage first
    const [language, setLanguageState] = useState(() => {
        try {
            const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (saved && (saved === 'fr' || saved === 'zh')) {
                return saved;
            }
        } catch (e) {
            console.error('Erreur lecture localStorage langue:', e);
        }
        return 'fr';
    });

    // When user logs in, if role is 'producteur' and no manual override was set, auto-switch to 'zh'
    useEffect(() => {
        try {
            const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (!saved && (isProducer || profile?.role === 'producteur')) {
                setLanguageState('zh');
                localStorage.setItem(LOCAL_STORAGE_KEY, 'zh');
            }
        } catch (e) {
            console.error('Erreur auto-détection langue:', e);
        }
    }, [isProducer, profile]);

    const setLanguage = (lang) => {
        if (lang === 'fr' || lang === 'zh') {
            setLanguageState(lang);
            try {
                localStorage.setItem(LOCAL_STORAGE_KEY, lang);
            } catch (e) {
                console.error('Erreur sauvegarde localStorage langue:', e);
            }
        }
    };

    const toggleLanguage = () => {
        setLanguage(language === 'fr' ? 'zh' : 'fr');
    };

    /**
     * Récupère la traduction pour une clé donnée (ex: 'dossier.title')
     * @param {string} key - Clé pointée dans le dictionnaire
     * @param {string} fallback - Texte de secours optionnel
     */
    const t = (key, fallback = '') => {
        if (!key) return fallback;
        const keys = key.split('.');
        
        let current = dictionaries[language];
        for (const k of keys) {
            if (current && current[k] !== undefined) {
                current = current[k];
            } else {
                current = null;
                break;
            }
        }

        if (current !== null && current !== undefined) {
            return current;
        }

        // Fallback vers le français si non trouvé dans la langue courante
        let frVal = dictionaries.fr;
        for (const k of keys) {
            if (frVal && frVal[k] !== undefined) {
                frVal = frVal[k];
            } else {
                frVal = null;
                break;
            }
        }

        return frVal !== null && frVal !== undefined ? frVal : (fallback || key);
    };

    /**
     * Traduit un statut (ex: 'Reçu', 'En production', 'Livré', etc.)
     */
    const translateStatus = (statusStr) => {
        if (!statusStr) return '';
        if (language === 'fr') return statusStr;

        const normalized = statusStr.toLowerCase().trim();
        switch (normalized) {
            case 'reçu':
            case 'recu':
                return dictionaries.zh.status.recu;
            case 'en cours':
            case 'en cours de fabrication':
                return dictionaries.zh.status.en_cours;
            case 'production':
            case 'en production':
                return dictionaries.zh.status.production;
            case 'en attente':
            case 'attente':
                return dictionaries.zh.status.en_attente;
            case 'terminé':
            case 'termine':
                return dictionaries.zh.status.termine;
            case 'expédié':
            case 'expedie':
                return dictionaries.zh.status.expedie;
            case 'livré':
            case 'livre':
                return dictionaries.zh.status.livre;
            case 'facturé':
            case 'facture':
                return dictionaries.zh.status.facture;
            case 'annulé':
            case 'annule':
                return dictionaries.zh.status.annule;
            case 'à refaire':
            case 'a refaire':
                return dictionaries.zh.status.a_refaire;
            default:
                return statusStr;
        }
    };

    return (
        <LanguageContext.Provider value={{
            language,
            isChinese: language === 'zh',
            setLanguage,
            toggleLanguage,
            t,
            translateStatus
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export default LanguageContext;
